import * as React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useCopyBudget } from '@/hooks/useBudgets';
import type { Budget } from '@/types';

interface CopyBudgetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceBudget?: Budget | null;
}

export function CopyBudgetDialog({
  open,
  onOpenChange,
  sourceBudget,
}: CopyBudgetDialogProps) {
  const copyBudget = useCopyBudget();
  const [newStartDate, setNewStartDate] = React.useState('');
  const [newEndDate, setNewEndDate] = React.useState('');
  const [newName, setNewName] = React.useState('');
  const [applyRollover, setApplyRollover] = React.useState(true);
  const [includeChildren, setIncludeChildren] = React.useState(true);

  React.useEffect(() => {
    if (!open) {
      setNewStartDate('');
      setNewEndDate('');
      setNewName('');
      setApplyRollover(true);
      setIncludeChildren(true);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!sourceBudget || !newStartDate || !newEndDate) return;

    await copyBudget.mutateAsync({
      sourceBudgetId: sourceBudget.id,
      newStartDate,
      newEndDate,
      newName: newName.trim() || undefined,
      applyRollover,
      includeChildren,
    });

    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Copy Budget</DialogTitle>
          <DialogDescription>
            Create a copy of &quot;{sourceBudget?.name ?? 'budget'}&quot; with a new date range.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Input
            label="New Budget Name (optional)"
            placeholder={sourceBudget?.name ?? 'Budget copy'}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="New Start Date"
              type="date"
              value={newStartDate}
              onChange={(e) => setNewStartDate(e.target.value)}
              required
            />
            <Input
              label="New End Date"
              type="date"
              value={newEndDate}
              onChange={(e) => setNewEndDate(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={applyRollover}
                onChange={(e) => setApplyRollover(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
              <span className="text-sm text-foreground">Apply rollover</span>
            </label>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={includeChildren}
                onChange={(e) => setIncludeChildren(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
              <span className="text-sm text-foreground">Include children</span>
            </label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={copyBudget.isPending}
            disabled={!sourceBudget || !newStartDate || !newEndDate}
          >
            Copy Budget
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}